import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import {
  AccordionDetails,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUser } from 'src/components/hooks/useUser';
import { sectionNames } from 'src/components/QueryMenus/checklistQueriesData';
import { QueryMenus } from 'src/components/QueryMenus/QueryMenus';
import { SectionQueryIndication } from 'src/components/QueryMenus/sectionQueryIndication';
import { useQueryMenus } from 'src/components/QueryMenus/useQueryMenus';
import { ViewQuery } from 'src/components/QueryMenus/ViewQuery/ViewQuery';
import { sendRequest } from 'src/hooks/useFetch';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
// import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { clearMessage, setMessage } from 'src/store/Message';
import { ISIVReportInformedConsentVerification } from 'src/types/SIVReport';
import { formatInput } from 'src/util/STR';
import { useFieldTranslation } from '../../fields/useFieldTranslation';
import {
  InformedConsentVerificationMandatory,
  ISIVReportMandatory,
  ISIVReportModifiedKeys,
  ISIVReportModifiedKeysOfKeys,
  MandatoryData
} from './useSivReport';
import { setIsSaveButtonDisable } from 'src/store/sivReport';

const NOTES_MAXLENGTH = 2000;

export interface IInformedConsentVerification {
  getSivReportData: (key: ISIVReportModifiedKeysOfKeys, add: boolean) => void;
  saveDisable: boolean;
  informedConsentVerificationData: ISIVReportInformedConsentVerification;
  updateIinformedConsentVerification: (data: ISIVReportInformedConsentVerification) => void;
  isUpdate: boolean;
  isChange: boolean;
  setIsChange: (step: ISIVReportModifiedKeys, key: ISIVReportModifiedKeysOfKeys, val: boolean) => void;
  updateSIVReportMandatoryData: (key: keyof ISIVReportMandatory, value: MandatoryData) => void;
  mandatory: InformedConsentVerificationMandatory;
  sivReportSavedId: string;
  checkedInStartDate: any;
  isPublished: boolean;
  SignatureBreaking: () => void;
}

const InformedConsentVerification: React.FunctionComponent<IInformedConsentVerification> = ({
  getSivReportData,
  saveDisable,
  isChange,
  setIsChange,
  informedConsentVerificationData,
  updateIinformedConsentVerification,
  isUpdate,
  updateSIVReportMandatoryData,
  mandatory,
  sivReportSavedId,
  checkedInStartDate,
  isPublished,
  SignatureBreaking
}) => {
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const user = useUser();
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const [protocolVersion, setProtocolVersion] = useState<any>('');
  const [recentData, setRecentData] = useState({ data: {}, mandatoryValidation: {} });
  const { checkPermission } = usePermissionWrapper();
  const isDisable = checkPermission('update_siv_report');
  const [refreshKey, setRefreshKey] = useState(0);
  const { sivcDataUpdateSuccessMsg, sivcMendatoryInfoErrorMsg, reportInformedConsent } = useFieldTranslation();
  const selectedRowData: any = useAppSelector(state => state.sivReport.selectedRowData);
  const saveButtonDisable: any = useAppSelector(state => state.sivReport.disableSaveButtonProps);
  const errorText = 'Required';
  const handleInformedConsentData = (e: any) => {
    dispatchApp(clearMessage());
    const val = e.target.value;
    const formattedVal = typeof val === 'string' ? formatInput(val) : val;
    const value = formattedVal;
    if (e.target.type === 'radio' && value === 'Y') {
      updateIinformedConsentVerification({
        ...informedConsentVerificationData,
        [e.target.name]: value
      });
      updateSIVReportMandatoryData('informedConsentVerification', { ...mandatory, [e.target.name]: false });
    } else if (e.target.type === 'radio') {
      if (e.target.name === 'siv_report_ic_process' && e.target.value === 'N') {
        updateIinformedConsentVerification({
          ...informedConsentVerificationData,
          siv_report_ic_template_translation: 'NA',
          [e.target.name]: value,
          [`${e.target.name}_comm`]: ''
        });
      } else {
        updateIinformedConsentVerification({
          ...informedConsentVerificationData,
          [e.target.name]: value
        });
      }
      updateSIVReportMandatoryData('informedConsentVerification', { ...mandatory, [e.target.name]: false });
    }
    if (saveButtonDisable && e.target.type === 'radio') {
      dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, informConcent: false }));
    }
    setConfirmExitValue(dispatch, true);
  };

  const handleChange = (e: any) => {
    if (e.target.type !== 'radio') {
      const val = e.target.value;
      const formattedVal = typeof val === 'string' ? formatInput(val) : val;
      const value = formattedVal;
      setRecentData({
        data: {
          ...recentData.data,
          [e.target.name]: value
        },
        mandatoryValidation: { ...recentData.mandatoryValidation, [e.target.name]: false }
      });
    }
    if (saveButtonDisable) {
      dispatch(setIsSaveButtonDisable(false));
    }
  };

  const setValidationData = () => {
    const copyValidation = { ...mandatory };
    for (const key of Object.keys(copyValidation)) {
      if (
        informedConsentVerificationData[key as keyof ISIVReportInformedConsentVerification] === 'N' &&
        !(
          informedConsentVerificationData[`${key}_comm` as keyof ISIVReportInformedConsentVerification] &&
          informedConsentVerificationData[`${key}_comm` as keyof ISIVReportInformedConsentVerification] !== null &&
          informedConsentVerificationData[`${key}_comm` as keyof ISIVReportInformedConsentVerification] !== ''
        )
      ) {
        if (!key.includes('siv_report_protocol_version')) {
          copyValidation[key as keyof InformedConsentVerificationMandatory] = true;
        }
      } else {
        copyValidation[key as keyof InformedConsentVerificationMandatory] = false;
      }
    }
    updateSIVReportMandatoryData('informedConsentVerification', copyValidation);
    return copyValidation;
  };
  useEffect(() => {
    if (Object.keys(recentData.data).length) {
      updateIinformedConsentVerification({
        ...informedConsentVerificationData,
        ...recentData.data
      });
      updateSIVReportMandatoryData('informedConsentVerification', { ...mandatory, ...recentData.mandatoryValidation });
    }
  }, [recentData.data]);
  const updateInformedConsentData = async () => {
    const validate = setValidationData();
    if (Object.values(validate).indexOf(true) !== -1) {
      return dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
    }
    const informedConsentVerificationDataTemp = informedConsentVerificationData;
    const options = {
      site_id: selectedSite,
      study_id: studyId,
      data: {
        updated_by: user.user,
        monitoring_visit_id: selectedRowData?.id,
        siv_report: { step_one: { informed_consent: informedConsentVerificationDataTemp } }
      },
      pathVariables: {
        country: selectedCountry,
        appName: process.env.REACT_APP_NAME
      }
    };
    try {
      if (isChange) {
        if (isUpdate) {
          if (informedConsentVerificationDataTemp.siv_report_ic_process === 'Y') {
            if (
              informedConsentVerificationDataTemp.siv_report_protocol_version !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_process !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_process_comm !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_template_translation !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_template_translation_comm !== null
            ) {
              await sendRequest('update_siv_report', options);
              if (isPublished) {
                SignatureBreaking();
              }
              getSivReportData('informedConsentVerification', false);
              setIsChange('Step1', 'informedConsentVerification', false);

              setConfirmExitValue(dispatch, false);
              dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
            } else {
              dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
            }
          }
          if (informedConsentVerificationDataTemp.siv_report_ic_process === 'N') {
            if (
              informedConsentVerificationDataTemp.siv_report_protocol_version !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_process !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_process_comm !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_sdv_process !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_sdv_process_comm !== null &&
              informedConsentVerificationDataTemp.siv_report_ic_template_translation_comm !== null
            ) {
              await sendRequest('update_siv_report', options);
              getSivReportData('informedConsentVerification', false);
              setIsChange('Step1', 'informedConsentVerification', false);
              setConfirmExitValue(dispatch, false);
              dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
            } else {
              dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
            }
          }
        }
        if (!isUpdate) {
          if (
            informedConsentVerificationDataTemp.siv_report_protocol_version !== null &&
            informedConsentVerificationDataTemp.siv_report_ic_process !== null &&
            informedConsentVerificationDataTemp.siv_report_ic_process_comm !== null &&
            informedConsentVerificationDataTemp.siv_report_ic_template_translation !== null &&
            informedConsentVerificationDataTemp.siv_report_ic_template_translation_comm !== null &&
            informedConsentVerificationDataTemp.siv_report_ic_sdv_process !== null &&
            informedConsentVerificationDataTemp.siv_report_ic_sdv_process_comm !== null
          ) {
            await sendRequest('create_siv_report', options);
            getSivReportData('informedConsentVerification', true);
            setIsChange('Step1', 'informedConsentVerification', false);

            dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
            setConfirmExitValue(dispatch, false);
          } else {
            dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
          }
        }
      } else {
        dispatchApp(setMessage({ msgDetails: 'No data modified', severity: 'info' }));
      }
      dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, informConcent: true }));
    } catch (e) {
      dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
    }
  };

  /**
   * Query menus code start
   */
  const {
    contextMenu,
    setContextMenu,
    queryKeys,
    setQueryKeys,
    checkListQueries,
    raisedQueries,
    totalFieldQueries,
    totalSectionQueries,
    fieldQueries,
    getSivReportQueries,
    raisedSectionQueries,
    report_id: reportId,
    setReport_id: setReportId,
    queryType,
    setQueryType,
    latestSecQueryRaisedBy
  } = useQueryMenus('SIV REPORT');

  useEffect(() => {
    setReportId(sivReportSavedId);
  }, [sivReportSavedId]);

  useEffect(() => {
    if (informedConsentVerificationData) {
      setRefreshKey(refreshKey + 1);
    }
  }, [Object.keys(informedConsentVerificationData)?.length]);

  const handleContextMenu = (event: React.MouseEvent, checklistItem: keyof ISIVReportInformedConsentVerification) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null &&
        (checkPermission('view_queries_siv_report') ||
          checkPermission('view_audit_siv_report') ||
          checkPermission('raise_query_siv_report'))
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4
          }
        : null
    );
    const sectionFieldNames: string[] = [];
    Object.keys(checkListQueries.informed_consent).forEach((obj: any) => {
      sectionFieldNames.push(checkListQueries.informed_consent[obj].report_field_name);
    });
    const newObj = {
      ...checkListQueries.informed_consent[checklistItem],
      id: sivReportSavedId,
      section_key: 'informed_consent',
      report_field_value: informedConsentVerificationData[checklistItem],
      section_field_names: sectionFieldNames,
      is_all_field_queries: true
    };
    setQueryKeys(newObj);
  };

  const [openViewQuery, setOpenViewQuery] = useState(false);
  const viewNotes = (checklistItem: keyof ISIVReportInformedConsentVerification, isAllFieldQueries = false) => {
    let newObj: any = {};
    if (isAllFieldQueries) {
      const sectionFieldNames: string[] = [];
      Object.keys(checkListQueries.informed_consent).forEach((obj: any) => {
        sectionFieldNames.push(checkListQueries.informed_consent[obj].report_field_name);
      });
      newObj = {
        ...checkListQueries.informed_consent[checklistItem],
        section_field_names: sectionFieldNames,
        id: sivReportSavedId,
        section_key: 'informed_consent',
        is_all_field_queries: isAllFieldQueries
      };
    } else {
      newObj = {
        ...checkListQueries.informed_consent[checklistItem],
        id: sivReportSavedId,
        section_key: 'informed_consent',
        report_field_value: informedConsentVerificationData[checklistItem],
        is_all_field_queries: isAllFieldQueries
      };
    }

    setQueryKeys(newObj);
    setOpenViewQuery(true);
    setQueryType('ALL');
  };
  const perDisableQuery = checkPermission('view_queries_siv_report');
  useEffect(() => {
    if (
      informedConsentVerificationData.siv_checklist_protocol_version_num == null ||
      informedConsentVerificationData.siv_checklist_protocol_version_num.toString() == ''
    ) {
      setProtocolVersion('x.xx');
    } else {
      setProtocolVersion(
        parseFloat(informedConsentVerificationData.siv_checklist_protocol_version_num.toString()).toFixed(1)
      );
    }
  }, [informedConsentVerificationData]);
  /**
   * Query menus code end
   */
  return (
    <>
      <AccordionDetails className="sq-checklist-tab protocol-compliance">
        <Grid container className="checklist-radio-actions">
          {raisedSectionQueries.includes(sectionNames.informed_consent) &&
            sectionNames.informed_consent in latestSecQueryRaisedBy &&
            SectionQueryIndication(
              latestSecQueryRaisedBy,
              sectionNames.informed_consent,
              viewNotes,
              'siv_report_protocol_version'
            )}
          <Grid md={12}>
            <Grid
              container
              onContextMenu={e => {
                handleContextMenu(e, 'siv_report_protocol_version');
              }}
              style={{ cursor: 'context-menu' }}
            >
              <Grid md={9}>
                {reportInformedConsent.label1} {protocolVersion} <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <FormControl>
                    <RadioGroup
                      value={informedConsentVerificationData.siv_report_protocol_version}
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="siv_report_protocol_version"
                      onChange={handleInformedConsentData}
                    >
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                  <Button
                    onClick={() => viewNotes('siv_report_protocol_version')}
                    disabled={!perDisableQuery}
                    title="Query"
                    className={`grey-btn${
                      raisedQueries.includes('siv_report_protocol_version') ||
                      raisedSectionQueries.includes(sectionNames.informed_consent)
                        ? ' danger-link'
                        : ''
                    }`}
                  >
                    <LiveHelpOutlinedIcon />
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="space-between" />
              </Grid>
              <Grid md={12} className="comment-section">
                <FormControl fullWidth margin="normal" className="siv-comment-input" />
              </Grid>
            </Grid>

            <Grid
              container
              onContextMenu={e => {
                handleContextMenu(e, 'siv_report_ic_process');
              }}
              style={{ cursor: 'context-menu' }}
            >
              <Grid md={9} className="alignCenterRow">
                {reportInformedConsent.label2}? <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <FormControl>
                    <RadioGroup
                      value={informedConsentVerificationData.siv_report_ic_process}
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="siv_report_ic_process"
                      onChange={handleInformedConsentData}
                    >
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                  <Button
                    onClick={() => viewNotes('siv_report_ic_process')}
                    disabled={!perDisableQuery}
                    title="Query"
                    className={`grey-btn${raisedQueries.includes('siv_report_ic_process') ? ' danger-link' : ''}`}
                  >
                    <LiveHelpOutlinedIcon />
                  </Button>
                </Stack>
              </Grid>
              <Grid md={12} className="comment-section">
                <FormControl fullWidth margin="normal" className="siv-comment-input">
                  <TextField
                    name="siv_report_ic_process_comm"
                    key={`siv_report_ic_process_comm-${refreshKey}`}
                    defaultValue={informedConsentVerificationData.siv_report_ic_process_comm || ''}
                    onBlur={handleInformedConsentData}
                    error={mandatory['siv_report_ic_process']}
                    helperText={mandatory['siv_report_ic_process'] ? errorText : ''}
                    label="Notes"
                    variant="outlined"
                    inputProps={{ maxLength: NOTES_MAXLENGTH }}
                    disabled={!isDisable || saveDisable || !checkedInStartDate}
                    onChange={handleChange}
                    multiline
                    minRows={1}
                    maxRows={10}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid
              container
              onContextMenu={e => {
                handleContextMenu(e, 'siv_report_ic_template_translation');
              }}
              style={{ cursor: 'context-menu' }}
            >
              <Grid md={9} className="alignCenterRow">
                {reportInformedConsent.label3}? <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <FormControl>
                    <RadioGroup
                      value={informedConsentVerificationData.siv_report_ic_template_translation}
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="siv_report_ic_template_translation"
                      onChange={handleInformedConsentData}
                    >
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                  <Button
                    onClick={() => viewNotes('siv_report_ic_template_translation')}
                    disabled={!perDisableQuery}
                    title="Query"
                    className={`grey-btn${
                      raisedQueries.includes('siv_report_ic_template_translation') ? ' danger-link' : ''
                    }`}
                  >
                    <LiveHelpOutlinedIcon />
                  </Button>
                </Stack>
              </Grid>
              <Grid md={12} className="comment-section">
                <FormControl fullWidth margin="normal" className="siv-comment-input">
                  <TextField
                    name="siv_report_ic_template_translation_comm"
                    key={`siv_report_ic_template_translation_comm-${refreshKey}`}
                    defaultValue={informedConsentVerificationData.siv_report_ic_template_translation_comm || ''}
                    onBlur={handleInformedConsentData}
                    error={mandatory['siv_report_ic_template_translation']}
                    helperText={mandatory['siv_report_ic_template_translation'] ? errorText : ''}
                    label="Notes"
                    variant="outlined"
                    inputProps={{ maxLength: NOTES_MAXLENGTH }}
                    disabled={!isDisable || saveDisable || !checkedInStartDate}
                    onChange={handleChange}
                    multiline
                    minRows={1}
                    maxRows={10}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid
              container
              onContextMenu={e => {
                handleContextMenu(e, 'siv_report_ic_sdv_process');
              }}
              style={{ cursor: 'context-menu' }}
            >
              <Grid md={9} className="alignCenterRow">
                {reportInformedConsent.label4}? <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <FormControl>
                    <RadioGroup
                      value={informedConsentVerificationData.siv_report_ic_sdv_process}
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="siv_report_ic_sdv_process"
                      onChange={handleInformedConsentData}
                    >
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                  <Button
                    onClick={() => viewNotes('siv_report_ic_sdv_process')}
                    disabled={!perDisableQuery}
                    title="Query"
                    className={`grey-btn${raisedQueries.includes('siv_report_ic_sdv_process') ? ' danger-link' : ''}`}
                  >
                    <LiveHelpOutlinedIcon />
                  </Button>
                </Stack>
              </Grid>
              <Grid md={12} className="comment-section">
                <FormControl fullWidth margin="normal" className="siv-comment-input">
                  <TextField
                    name="siv_report_ic_sdv_process_comm"
                    key={`siv_report_ic_sdv_process_comm-${refreshKey}`}
                    defaultValue={informedConsentVerificationData.siv_report_ic_sdv_process_comm || ''}
                    onBlur={handleInformedConsentData}
                    error={mandatory['siv_report_ic_sdv_process']}
                    helperText={mandatory['siv_report_ic_sdv_process'] ? errorText : ''}
                    label="Notes"
                    variant="outlined"
                    inputProps={{ maxLength: NOTES_MAXLENGTH }}
                    disabled={!isDisable || saveDisable || !checkedInStartDate}
                    onChange={handleChange}
                    multiline
                    minRows={1}
                    maxRows={10}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
      <Grid container className="fixed-buttons-block">
        {checkPermission('update_siv_report') ? (
          <Grid md={12} display="flex" justifyContent="flex-end">
            <Stack spacing={2} direction="row">
              {isDisable ? (
                <Button
                  onClick={updateInformedConsentData}
                  variant="text"
                  disabled={saveButtonDisable?.informConcent || !checkedInStartDate}
                >
                  SAVE
                </Button>
              ) : (
                ''
              )}{' '}
            </Stack>
          </Grid>
        ) : (
          ''
        )}
        <QueryMenus
          totalFieldQueries={totalFieldQueries}
          totalSectionQueries={totalSectionQueries}
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          queryKeys={queryKeys}
          fieldQueries={fieldQueries}
          getSivReportQueries={getSivReportQueries}
          report_id={reportId}
          checkListQueries={checkListQueries}
          queryType={queryType}
          setQueryType={setQueryType}
          isSQSivQueries
        />
        <ViewQuery
          totalFieldQueries={totalFieldQueries}
          totalSectionQueries={totalSectionQueries}
          open={openViewQuery}
          setOpen={setOpenViewQuery}
          queryKeys={queryKeys}
          queryType={queryType}
          fieldQueries={fieldQueries}
          getSivReportQueries={getSivReportQueries}
          setQueryType={setQueryType}
          report_id={reportId}
          isSQSivQueries
        />
      </Grid>
    </>
  );
};

export default InformedConsentVerification;
